import { Formik, Form, Field, FieldArray } from 'formik'
import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axiosService from '../../../utils/AxiosService'
import Input from '../../FormComponents/Input'
import { CheckLg, Image, Plus, Trash } from 'react-bootstrap-icons'
import InstagramButton from '../Shared/ProviderButtons/InstagramButton'
import TikTokButton from '../Shared/ProviderButtons/TikTokButton'
import YouTubeButton from '../Shared/ProviderButtons/YouTubeButton'
import LinkedInButton from '../Shared/ProviderButtons/LinkedInButton'
import PinterestButton from '../Shared/ProviderButtons/PinterestButton'
import PodcastButton from '../Shared/ProviderButtons/PodcastButton'
import OtherButton from '../Shared/ProviderButtons/OtherButton'
import FileDropzone from '../../FormComponents/FileDropzone'
import { toastSuccess, toastError } from '../../FormComponents/Toasts'
import ReactSelect from '../../FormComponents/ReactSelect'
import CurrencySelect from '../../FormComponents/CurrencySelect'
import Label from '../../FormComponents/Label'
import Checkbox from '../../FormComponents/Checkbox'
import { CampaignTargetSchema } from '../../../schemas/CampaignTargetSchema'

const READONLY_WRAP = 'pointer-events-none opacity-60 cursor-not-allowed'
const EMPTY_CUSTOM_FIELD = { label: '', field_type: '', advertiser_fee_enabled: true, ksa_enabled: true }

const normalizePlatforms = (platforms) => {
  if (!platforms) return []
  if (Array.isArray(platforms)) return platforms
  return [...platforms]
}

const normalizeCustomFields = (customFields) => {
  if (!Array.isArray(customFields) || customFields.length === 0) return [{ ...EMPTY_CUSTOM_FIELD }]

  return customFields.map((field) => ({
    id: field.id,
    label: field.label || '',
    field_type: field.field_type || 'text_field',
    advertiser_fee_enabled: field.advertiser_fee_enabled ?? true,
    ksa_enabled: field.ksa_enabled ?? true,
    system_key: field.system_key || null
  }))
}

const normalizeDateInput = (value) => {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''

  const year = parsed.getFullYear()
  const month = `${parsed.getMonth() + 1}`.padStart(2, '0')
  const day = `${parsed.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

const EditCampaign = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [campaign, setCampaign] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [uploadedImagePreview, setUploadedImagePreview] = useState(null)

  const themeColors = useMemo(() => ([
    '#E54747',
    '#E59D47',
    '#E9E12F',
    '#A0E547',
    '#47E590',
    '#47E5E5',
    '#6747E5',
    '#BC47E5',
    '#E547A6'
  ]), [])
  const customFieldTypeOptions = useMemo(() => ([
    { value: 'note', label: 'Note' },
    { value: 'numeric', label: 'Number' },
    { value: 'cost_item', label: 'Cost item' }
  ]), [])

  useEffect(() => {
    axiosService.get(`/api/project_manager/campaigns/${id}`)
      .then((data) => {
        if (data.archived_at) {
          navigate(`/project_manager/campaigns/${id}/overview`, { replace: true })
          return
        }
        setCampaign(data)
        setUploadedImagePreview((prev) => {
          if (prev) URL.revokeObjectURL(prev)
          return null
        })
        setLoadError(null)
      })
      .catch(() => {
        setLoadError(true)
        toastError('Could not load campaign')
        navigate('/project_manager/campaigns')
      })
  }, [id, navigate])

  useEffect(() => () => {
    if (uploadedImagePreview) URL.revokeObjectURL(uploadedImagePreview)
  }, [uploadedImagePreview])

  const handleSubmit = (values) => {
    const visibleCustomFields = values.custom_fields.filter((field) => !field._destroy)
    const hasInvalidCustomField = visibleCustomFields.some((field) => {
      const label = (field.label || '').trim()
      const hasCustomFieldData = Boolean(field.id || label || field.field_type)

      if (!hasCustomFieldData) return false

      return !label
    })

    if (hasInvalidCustomField) {
      toastError('Custom fields need a label.')
      return
    }

    const customFieldsAttributes = values.custom_fields.reduce((result, field) => {
      if (field._destroy) {
        if (field.id) result.push({ id: field.id, _destroy: true })
        return result
      }

      const label = (field.label || '').trim()
      if (!label) return result

      result.push({
        ...(field.id ? { id: field.id } : {}),
        label,
        field_type: field.field_type || 'text_field',
        advertiser_fee_enabled: !!field.advertiser_fee_enabled,
        ksa_enabled: !!field.ksa_enabled
      })
      return result
    }, [])

    const campaignScalars = {
      start_date: values.start_date,
      end_date: values.end_date,
      currency: values.currency,
      currency_symbol: values.currency_symbol,
      gross_reach_target: values.gross_reach_target,
      net_reach_target: values.net_reach_target,
      impressions_target: values.impressions_target,
      influencer_target: values.influencer_target,
      action_count_target: values.action_count_target,
      influencer_budget: values.influencer_budget,
      social_budget: values.social_budget,
      media_budget: values.media_budget,
      travel_cost_enabled: values.travel_cost_enabled,
      travel_cost_target: values.travel_cost_target,
      attendance_cost_enabled: values.attendance_cost_enabled,
      attendance_cost_target: values.attendance_cost_target,
      theme_color: values.theme_color
    }

    const appendCustomFieldsToFormData = (formData) => {
      customFieldsAttributes.forEach((field, index) => {
        if (field.id) formData.append(`campaign[campaign_custom_fields_attributes][${index}][id]`, field.id)
        if (field._destroy) {
          formData.append(`campaign[campaign_custom_fields_attributes][${index}][_destroy]`, '1')
          return
        }
        formData.append(`campaign[campaign_custom_fields_attributes][${index}][label]`, field.label)
        formData.append(`campaign[campaign_custom_fields_attributes][${index}][field_type]`, field.field_type)
        formData.append(`campaign[campaign_custom_fields_attributes][${index}][advertiser_fee_enabled]`, field.advertiser_fee_enabled ? '1' : '0')
        formData.append(`campaign[campaign_custom_fields_attributes][${index}][ksa_enabled]`, field.ksa_enabled ? '1' : '0')
      })
    }

    const saveRequest = values.image instanceof File
      ? (() => {
          const formData = new FormData()
          Object.entries(campaignScalars).forEach(([key, val]) => {
            formData.append(`campaign[${key}]`, val === '' || val == null ? '' : val)
          })
          values.platforms.forEach((platform) => formData.append('campaign[platforms][]', platform))
          formData.append('campaign[image]', values.image)
          appendCustomFieldsToFormData(formData)
          return axiosService.patch(`/api/project_manager/campaigns/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        })()
      : axiosService.patch(`/api/project_manager/campaigns/${id}`, {
          campaign: {
            ...campaignScalars,
            platforms: values.platforms,
            campaign_custom_fields_attributes: customFieldsAttributes
          }
        })

    saveRequest
      .then(() => {
        toastSuccess('Campaign saved')
        navigate(`/project_manager/campaigns/${id}/overview`)
      })
      .catch(({ data: { error } = {} }) => toastError(error || 'Could not save campaign'))
  }

  if (loadError || !campaign) {
    return (
      <div className='flex h-full w-full bg-rb-gre items-center justify-center'>
        <p className='rb-text-secondary'>{loadError ? '' : 'Loading…'}</p>
      </div>
    )
  }

  const platforms = normalizePlatforms(campaign.platforms)
  const clientOptions = [{ label: campaign.client_name || '—', value: campaign.client_id }]
  const folderLabel = campaign.folder_name || 'No folder'
  const folderSelectValue = campaign.folder_id != null ? campaign.folder_id : '__none__'
  const folderOptions = [{ label: folderLabel, value: folderSelectValue }]
  const ownerOptions = [{ label: campaign.owner_name || '—', value: campaign.project_manager_id }]

  return (
    <div className='flex h-full w-full bg-rb-gre'>
      <div className='mx-auto max-w-screen-lg h-full pt-20'>
        <div className="min-w-[600px] h-full">
          <h1 className='font-bold text-2xl mb-5'>Edit campaign</h1>
          <Formik
            enableReinitialize
            initialValues={{
              name: campaign.name || '',
              project_number: campaign.project_number || '',
              project_manager_id: campaign.project_manager_id,
              client_id: campaign.client_id,
              folder_id: folderSelectValue,
              currency: campaign.currency || 'EUR',
              currency_symbol: campaign.currency_symbol || '€',
              platforms,
              theme_color: campaign.theme_color || themeColors[0],
              image: '',
              gross_reach_target: campaign.gross_reach_target ?? '',
              net_reach_target: campaign.net_reach_target ?? '',
              impressions_target: campaign.impressions_target ?? '',
              influencer_target: campaign.influencer_target ?? '',
              action_count_target: campaign.action_count_target ?? '',
              influencer_budget: campaign.influencer_budget ?? '',
              social_budget: campaign.social_budget ?? '',
              media_budget: campaign.media_budget ?? '',
              travel_cost_enabled: campaign.travel_cost_enabled ?? false,
              travel_cost_target: campaign.travel_cost_target ?? '',
              attendance_cost_enabled: campaign.attendance_cost_enabled ?? false,
              attendance_cost_target: campaign.attendance_cost_target ?? '',
              custom_fields: normalizeCustomFields(campaign.custom_fields),
              start_date: normalizeDateInput(campaign.start_date),
              end_date: normalizeDateInput(campaign.end_date)
            }}
            validationSchema={CampaignTargetSchema}
            onSubmit={handleSubmit}
          >
            {({ handleBlur, handleChange, values, touched, errors, setFieldValue }) => (
              <Form>
                <div className="form-item-container w-full flex flex-col gap-8">

                  <section className="bg-white rounded-lg p-5 border-1 border-rb-widget-boarder dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter">
                    <h3 className='font-bold text-lg'>General</h3>
                    <div className={`grid grid-cols-2 gap-4 mt-4 ${READONLY_WRAP}`} aria-readonly="true">
                      <div>
                        <Input
                          error={touched.name && errors.name}
                          noLabelPadding
                          label="Name"
                          name='name'
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.name}
                          topMargin='mt-1'
                          disabled
                        />
                      </div>
                      <div className='pt-1'>
                        <ReactSelect
                          error={touched.project_manager_id && errors.project_manager_id}
                          isClearable={false}
                          name="project_manager_id"
                          onChange={() => {}}
                          onBlur={handleBlur}
                          value={values.project_manager_id}
                          options={ownerOptions}
                          label='Owner'
                          noLabelPadding
                          disabled
                        />
                      </div>
                      <div>
                        <Input
                          error={touched.project_number && errors.project_number}
                          noLabelPadding
                          label="Project Number"
                          name='project_number'
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.project_number}
                          topMargin='mt-1'
                          disabled
                        />
                      </div>
                    </div>
                    <p className='text-sm rb-text-secondary mb-1 mt-4'>These fields cannot be changed after the campaign is created.</p>
                    <div className='pt-1 mt-4 max-w-[calc(50%-0.5rem)]'>
                      <div className='mb-1'>
                        <Label label='CampaignCurrency' noLabelPadding />
                      </div>
                      <CurrencySelect
                        error={touched.currency && errors.currency}
                        isClearable={false}
                        name="currency"
                        onChange={(option) => {
                          setFieldValue('currency', option.value, true)
                          setFieldValue('currency_symbol', option.symbol)
                        }}
                        onBlur={handleBlur}
                        value={values.currency}
                      />
                    </div>
                  </section>

                  <section className={`bg-white rounded-lg p-5 border-1 border-rb-widget-boarder dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter ${READONLY_WRAP}`} aria-readonly="true">
                    <h3 className='font-bold text-lg'>Client</h3>
                    <p className='rb-text-secondary mb-4'>Client cannot be changed after the campaign is created.</p>
                    <ReactSelect
                      error={touched.client_id && errors.client_id}
                      isClearable={false}
                      name="client_id"
                      onChange={() => {}}
                      onBlur={handleBlur}
                      value={values.client_id}
                      options={clientOptions}
                      disabled
                    />
                  </section>

                  <section className={`bg-white rounded-lg p-5 border-1 border-rb-widget-boarder w-full dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter ${READONLY_WRAP}`} aria-readonly="true">
                    <h3 className='font-bold text-lg'>Folder</h3>
                    <p className="text-md font-normal rb-text-secondary pb-4">Folder cannot be changed on this screen.</p>
                    <div className='mt-3 w-full'>
                      <ReactSelect
                        error={touched.folder_id && errors.folder_id}
                        isClearable={false}
                        name="folder_id"
                        onChange={() => {}}
                        onBlur={handleBlur}
                        value={values.folder_id}
                        options={folderOptions}
                        disabled
                      />
                    </div>
                  </section>

                  <div className="bg-white rounded-lg p-5 border-1 border-rb-widget-boarder dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter">
                    <h3 className='font-bold text-lg'>Platforms</h3>
                    <p className="text-md font-normal rb-text-secondary pb-4">Select the platforms that are included in your campaign.</p>
                    <div role="group" className="flex flex-wrap justify-center gap-2 mx-auto rounded-lg bg-gray-200 w-96 p-2 dark:bg-rb-dark-mode-lighter">
                      <InstagramButton
                        variant="checkbox"
                        active={values.platforms.includes('instagram')}
                        name="platforms"
                        value="instagram"
                      />
                      <TikTokButton
                        variant="checkbox"
                        active={values.platforms.includes('tiktok')}
                        name="platforms"
                        value="tiktok"
                      />
                      <YouTubeButton
                        variant="checkbox"
                        active={values.platforms.includes('youtube')}
                        name="platforms"
                        value="youtube"
                      />
                      <PinterestButton
                        variant="checkbox"
                        active={values.platforms.includes('pinterest')}
                        name="platforms"
                        value="pinterest"
                      />
                      <LinkedInButton
                        variant="checkbox"
                        active={values.platforms.includes('linkedin')}
                        name="platforms"
                        value="linkedin"
                      />
                      <PodcastButton
                        variant="checkbox"
                        active={values.platforms.includes('podcast')}
                        name="platforms"
                        value="podcast"
                      />
                      <OtherButton
                        variant="checkbox"
                        active={values.platforms.includes('other')}
                        name="platforms"
                        value="other"
                      />
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-5 border-1 border-rb-widget-boarder dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter">
                    <h3 className='font-bold text-lg'>Targets</h3>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block mb-1 text-sm font-medium text-rb-black dark:text-rb-dark-mode-text-grey">
                          Campaign Start Date
                        </label>
                        <input
                          type="date"
                          name="start_date"
                          value={values.start_date}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="rb-date-input h-11 w-full rounded-lg border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter px-4 bg-white dark:bg-rb-dark-mode text-rb-black dark:text-rb-dark-mode-text-grey [color-scheme:light] dark:[color-scheme:dark] focus-visible:outline-0 focus:border-rb-green focus:border-2"
                        />
                        {touched.start_date && errors.start_date && (
                          <p className="text-red-500 text-[10px] mt-1 px-2">{errors.start_date}</p>
                        )}
                      </div>
                      <div>
                        <label className="block mb-1 text-sm font-medium text-rb-black dark:text-rb-dark-mode-text-grey">
                          Campaign End Date
                        </label>
                        <input
                          type="date"
                          name="end_date"
                          min={values.start_date || undefined}
                          value={values.end_date}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="rb-date-input h-11 w-full rounded-lg border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter px-4 bg-white dark:bg-rb-dark-mode text-rb-black dark:text-rb-dark-mode-text-grey [color-scheme:light] dark:[color-scheme:dark] focus-visible:outline-0 focus:border-rb-green focus:border-2"
                        />
                        {touched.end_date && errors.end_date && (
                          <p className="text-red-500 text-[10px] mt-1 px-2">{errors.end_date}</p>
                        )}
                      </div>
                      <div>
                        <Input error={touched.gross_reach_target && errors.gross_reach_target} noLabelPadding label="Gross Reach" placeholder="Gross Reach" name="gross_reach_target" onChange={handleChange} onBlur={handleBlur} value={values.gross_reach_target} />
                      </div>
                      <div>
                        <Input error={touched.net_reach_target && errors.net_reach_target} noLabelPadding label="Net Reach" placeholder="Net Reach" name="net_reach_target" onChange={handleChange} onBlur={handleBlur} value={values.net_reach_target} />
                      </div>
                      <div>
                        <Input error={touched.impressions_target && errors.impressions_target} noLabelPadding label="Impressions" placeholder="Impressions" name="impressions_target" onChange={handleChange} onBlur={handleBlur} value={values.impressions_target} />
                      </div>
                      <div>
                        <Input error={touched.influencer_target && errors.influencer_target} noLabelPadding label="Influencers" placeholder="Influencer count" name="influencer_target" onChange={handleChange} onBlur={handleBlur} value={values.influencer_target} />
                      </div>
                      <div>
                        <Input error={touched.influencer_budget && errors.influencer_budget} noLabelPadding label="Influencer Budget" placeholder="Influencer budget" name="influencer_budget" onChange={handleChange} onBlur={handleBlur} value={values.influencer_budget} />
                      </div>
                      <div>
                        <Input error={touched.social_budget && errors.social_budget} noLabelPadding label="Social Budget" placeholder="Social ads budget" name="social_budget" onChange={handleChange} onBlur={handleBlur} value={values.social_budget} />
                      </div>
                      <div>
                        <Input error={touched.media_budget && errors.media_budget} noLabelPadding label="Buyout Budget" placeholder="Buyout budget" name="media_budget" onChange={handleChange} onBlur={handleBlur} value={values.media_budget} />
                      </div>
                      <div>
                        <Input error={touched.action_count_target && errors.action_count_target} noLabelPadding label="Number of Actions" placeholder="Number of Actions" name="action_count_target" onChange={handleChange} onBlur={handleBlur} value={values.action_count_target} />
                      </div>
                    </div>

                    <div className="mt-6 pt-4 border-t-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter">
                      <p className="font-bold text-sm text-rb-black dark:text-white mb-3">Additional cost widgets</p>
                      <p className="text-sm rb-text-secondary mb-3">Enable to show a Target and Status widget for this cost, with a matching entry in the Budget Table's Sonstiges section.</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Checkbox
                            label="Travel Costs"
                            name="travel_cost_enabled"
                            checked={!!values.travel_cost_enabled}
                            onChange={(e) => setFieldValue('travel_cost_enabled', e.target.checked, false)}
                          />
                          {values.travel_cost_enabled && (
                            <div className="mt-2">
                              <Input
                                addon={values.currency_symbol || '€'}
                                addonPosition="start"
                                className='px-2'
                                error={touched.travel_cost_target && errors.travel_cost_target}
                                noLabelPadding
                                label="Travel Costs Target"
                                placeholder="Travel costs budget"
                                name="travel_cost_target"
                                onChange={(e) => setFieldValue('travel_cost_target', e.target.value.replace(/,/g, '.'), true)}
                                onBlur={handleBlur}
                                value={values.travel_cost_target}
                              />
                            </div>
                          )}
                        </div>
                        <div>
                          <Checkbox
                            label="Event Attendance"
                            name="attendance_cost_enabled"
                            checked={!!values.attendance_cost_enabled}
                            onChange={(e) => setFieldValue('attendance_cost_enabled', e.target.checked, false)}
                          />
                          {values.attendance_cost_enabled && (
                            <div className="mt-2">
                              <Input
                                addon={values.currency_symbol || '€'}
                                addonPosition="start"
                                className='px-2'
                                error={touched.attendance_cost_target && errors.attendance_cost_target}
                                noLabelPadding
                                label="Event Attendance Target"
                                placeholder="Event attendance budget"
                                name="attendance_cost_target"
                                onChange={(e) => setFieldValue('attendance_cost_target', e.target.value.replace(/,/g, '.'), true)}
                                onBlur={handleBlur}
                                value={values.attendance_cost_target}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-6 border-1 border-rb-widget-boarder dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter">
                    <h3 className="font-bold text-xl text-rb-black dark:text-white mb-6">Budget Table Settings</h3>
                    <p className="font-bold text-md text-rb-black dark:text-white mb-4">Custom columns</p>
                    <FieldArray name="custom_fields">
                      {({ remove, push }) => (
                        <div className="flex flex-col gap-4">
                          {values.custom_fields.map((field, index) => {
                            if (field._destroy) return null

                            return (
                              <div
                                key={field.id || `new-${index}`}
                                className="rounded-xl border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter bg-white dark:bg-rb-dark-mode p-4 shadow-sm"
                              >
                                <div className="flex flex-row items-end gap-3 w-full">
                                  <div className="flex-1 campaign-custom-field-type-select react-select-white-bg min-w-0">
                                    <ReactSelect
                                      label="Type"
                                      noLabelPadding
                                      isClearable={false}
                                      isSearchable={false}
                                      name={`custom_fields.${index}.field_type`}
                                      value={values.custom_fields[index].field_type}
                                      options={customFieldTypeOptions}
                                      onChange={(option) => {
                                        setFieldValue(`custom_fields.${index}.field_type`, option?.value || 'text_field', false)
                                      }}
                                      onBlur={handleBlur}
                                      placeholder="Select type"
                                    />
                                  </div>

                                  <div className="flex-[1.2] min-w-0">
                                    <Input
                                      label="Label"
                                      topMargin="mt-0"
                                      noLabelPadding
                                      name={`custom_fields.${index}.label`}
                                      value={values.custom_fields[index].label}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      placeholder="Field label"
                                      addonPosition="whatever"
                                    />
                                  </div>

                                  <div className="flex-none">
                                    <button
                                      type="button"
                                      className="h-11 w-11 rounded-lg bg-rb-grey dark:bg-rb-dark-mode-lighter flex items-center justify-center rb-text-secondary border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter hover:bg-gray-200 dark:hover:bg-rb-dark-mode flex-shrink-0"
                                      onClick={() => {
                                        if (values.custom_fields[index].id) {
                                          setFieldValue(`custom_fields.${index}._destroy`, true, false)
                                        } else {
                                          remove(index)
                                        }

                                        const remainingVisible = values.custom_fields.filter((item, itemIndex) => itemIndex !== index && !item._destroy).length
                                        if (remainingVisible === 0) push({ ...EMPTY_CUSTOM_FIELD })
                                      }}
                                      aria-label="Delete custom field"
                                    >
                                      <Trash />
                                    </button>
                                  </div>
                                </div>

                                {values.custom_fields[index].field_type === 'cost_item' && (
                                  values.custom_fields[index].system_key ? (
                                    <p className="text-sm rb-text-secondary mt-3">Entered value is used as-is (no fee/KSA multiplier) - managed via the "Additional cost widgets" checkbox above.</p>
                                  ) : (
                                    <div className="flex flex-row gap-6 mt-3">
                                      <Checkbox
                                        label="Apply 20% fee"
                                        name={`custom_fields.${index}.advertiser_fee_enabled`}
                                        checked={!!values.custom_fields[index].advertiser_fee_enabled}
                                        onChange={(e) => setFieldValue(`custom_fields.${index}.advertiser_fee_enabled`, e.target.checked, false)}
                                      />
                                      <Checkbox
                                        label="Apply KSA"
                                        name={`custom_fields.${index}.ksa_enabled`}
                                        checked={!!values.custom_fields[index].ksa_enabled}
                                        onChange={(e) => setFieldValue(`custom_fields.${index}.ksa_enabled`, e.target.checked, false)}
                                      />
                                    </div>
                                  )
                                )}
                              </div>
                            )
                          })}

                          <button
                            type="button"
                            className="w-full rounded-lg border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter bg-rb-grey dark:bg-rb-dark-mode-lighter py-4 px-4 font-bold text-rb-black dark:text-white flex items-center justify-center gap-2 hover:bg-gray-200 dark:hover:bg-rb-dark-mode mt-2"
                            onClick={() => push({ ...EMPTY_CUSTOM_FIELD })}
                          >
                            <Plus className="shrink-0 text-xl" />
                            Add New Field
                          </button>
                        </div>
                      )}
                    </FieldArray>
                  </div>

                  <div className="bg-white rounded-lg p-5 border-1 border-rb-widget-boarder dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter">
                    <h3 className='font-bold text-lg'>Content</h3>
                    <p className='text-rb-mat-grey text-md'>Select a color for your campaign thumbnail, or upload an image.</p>
                    <div className='flex justify-between items-center mt-5'>
                      <div>
                        <div className="flex px-2">
                          {themeColors.map((color, index) => (
                            <label
                              key={index}
                              className="cursor-pointer h-8 w-8 rounded-full border-1 border-white -ml-2 flex justify-center items-center"
                              style={{ backgroundColor: color }}
                              aria-label={color}
                            >
                              <Field type="radio" name="theme_color" value={color} className="hidden" />
                              {values.theme_color === color ? <CheckLg className='text-white text-xl' /> : ''}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="h-16 w-30 rounded-md p-3 flex justify-between items-center border-1 border-rb-ligher-grey">
                        <div className="h-12 w-12 mr-2 rounded-md p-4 font-bold flex items-center justify-center border-1 border-rb-lighter-grey" style={{ backgroundColor: values.theme_color || themeColors[0] }}>
                          <span>{(values.name || '').slice(0, 2).toUpperCase() || 'TP'}</span>
                        </div>
                        <p className='w-[150px] truncate font-bold'>Thumbnail preview</p>
                      </div>
                    </div>

                    <div className='mt-5'>
                      <p className='text-rb-mat-grey text-md mb-3'>Campaign image</p>
                      <FileDropzone
                        uploadFiles={(files) => {
                          const file = files[0]
                          setUploadedImagePreview((prev) => {
                            if (prev) URL.revokeObjectURL(prev)
                            return URL.createObjectURL(file)
                          })
                          setFieldValue('image', file, false)
                        }}
                        icon={<Image />}
                        key='campaign_upload_edit'
                      />
                      <div className={`w-full flex justify-center ${(uploadedImagePreview || campaign.image) ? 'py-6' : 'py-1'}`}>
                        {(uploadedImagePreview || campaign.image) && (
                          <img className="h-20 w-20 rounded-3xl object-cover" src={uploadedImagePreview || campaign.image} alt="" />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between mt-1">
                    <button
                      type='button'
                      className="rb-btn-cancel text-lg p-2 px-4 rounded-lg mr-2"
                      onClick={() => navigate(`/project_manager/campaigns/${id}/overview`)}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="ml-auto bg-rb-green text-white rounded-lg p-2 px-4 submit text-center active:shadow-outer active:shadow-rb-green border-rb-green dark:bg-rb-green dark:text-rb-dark-mode dark:border-rb-green"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  )
}

export default EditCampaign
